import { jwtDecode } from 'jwt-decode';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CLOUDINARY_URL, APPOINTMENT_STATUS } from '../constants/constants.js';

// Configure dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

// Eastern Time zone constant
const EASTERN_TIMEZONE = 'America/New_York';

/**
 * Formats a date/time to Eastern Time with the specified format
 * @param {string|Date} date - The date to format
 * @param {string} format - The dayjs format string
 * @returns {string} - Formatted date string in Eastern Time
 */
const formatToEastern = (date, format) => {
  if (!date) return '-';
  return dayjs(date).tz(EASTERN_TIMEZONE).format(format);
};

/**
 * Formats a time to Eastern Time with "ET" indicator
 * @param {string|Date} date - The date to format
 * @param {string} format - The dayjs format string (default: 'h:mm A')
 * @returns {string} - Formatted time string with ET indicator
 */
const formatTimeET = (date, format = 'h:mm A') => {
  if (!date) return '-';
  return `${dayjs(date).tz(EASTERN_TIMEZONE).format(format)} ET`;
};

/**
 * Formats a date to Eastern Time
 * @param {string|Date} date - The date to format
 * @param {string} format - The dayjs format string (default: 'MMM D, YYYY')
 * @returns {string} - Formatted date string in Eastern Time
 */
const formatDateET = (date, format = 'MMM D, YYYY') => {
  if (!date) return '-';
  return dayjs(date).tz(EASTERN_TIMEZONE).format(format);
};

/**
 * Formats a date and time to Eastern Time with ET indicator
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted date and time string with ET indicator
 */
const formatDateTimeET = date => {
  if (!date) return '-';
  return `${dayjs(date).tz(EASTERN_TIMEZONE).format('MMM D, YYYY h:mm A')} ET`;
};

/**
 * Gets a dayjs object in Eastern Time
 * @param {string|Date} date - The date to convert
 * @returns {dayjs.Dayjs} - dayjs object in Eastern Time
 */
const toEasternTime = date => {
  return dayjs(date).tz(EASTERN_TIMEZONE);
};

const uploadImage = async (image, fileName) => {
  let url;
  const data = new FormData();
  data.append('file', image);
  data.append('upload_preset', 'bjy7qgah');
  data.append('cloud_name', 'dol6pnjxb');
  await fetch(CLOUDINARY_URL, {
    method: 'POST',
    body: data,
  })
    .then(res => res.json())
    .then(data => {
      url = data.secure_url;
    })
    .catch(err => {
      err = err;
    });
  return url;
};

const addKeyToStorage = (key, value) => {
  localStorage.setItem(key, value);
};

const deleteKeyFromStorage = key => {
  localStorage.removeItem(key);
};

const updateKey = (key, value) => {
  localStorage.setItem(key, value);
};

const getValueFromStorage = key => {
  const value = localStorage.getItem(key);
  return value;
};

const getUserInfo = () => {
  try {
    let user = jwtDecode(getValueFromStorage('token'));
    return user;
  } catch (err) {
    return err;
  }
};

/**
 * Determines if an appointment can be invoiced based on its status
 * @param {string} status - The appointment status
 * @returns {boolean} - Returns true if the appointment can be invoiced, false otherwise
 */
const isInvoicable = status => {
  if (!status) return false;

  const nonInvoicableStatuses = [
    APPOINTMENT_STATUS.REJECTED,
    APPOINTMENT_STATUS.PENDING,
    APPOINTMENT_STATUS.CANCELLED,
  ];

  return !nonInvoicableStatuses.includes(status);
};

/**
 * Sorts a list of dogs alphabetically by name, case-insensitively.
 * Accepts both API shapes in use: `dog_name` (dog records) and `name`
 * (dashboard rows). Returns a new array, so it is safe to call directly on
 * RTK Query results, which are frozen.
 * @param {Array} dogs - The dogs to sort
 * @returns {Array} - A new alphabetically sorted array (empty if not an array)
 */
const sortDogsByName = dogs => {
  if (!Array.isArray(dogs)) return [];

  const nameOf = dog => dog?.dog_name || dog?.name || '';

  return [...dogs].sort((a, b) =>
    nameOf(a).localeCompare(nameOf(b), undefined, {
      sensitivity: 'base',
      numeric: true,
    })
  );
};

export {
  addKeyToStorage,
  deleteKeyFromStorage,
  updateKey,
  getValueFromStorage,
  getUserInfo,
  uploadImage,
  formatToEastern,
  formatTimeET,
  formatDateET,
  formatDateTimeET,
  toEasternTime,
  EASTERN_TIMEZONE,
  isInvoicable,
  sortDogsByName,
};
